import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

import { AuthContext, User } from 'app/model';
import { Taxonomy } from 'app/model/taxonomy';
import { authenticationService } from 'app/services/authentication-service';
import { userService } from 'app/services/user.service';
import { AppThunk, RootState } from 'app/store';
import { SecurityUtils } from 'app/utils/security-utils';

// Define a type for the slice state
export interface AuthState {
    loggedIn: boolean;
    roles: Record<string, boolean>;
    profile?: User;
    activeTaxonomies?: Taxonomy[];
}

const context = SecurityUtils.buildAuthContext();

// Define the initial state using that type
const initialState: AuthState = {
    loggedIn: context.valid,
    roles: context.roles,
    activeTaxonomies: SecurityUtils.getStoredTaxonomies(),
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setActiveUser: (state, action: PayloadAction<AuthContext>) => {
            state.loggedIn = action.payload.valid;
            state.roles = action.payload.roles;
        },
        logoutUser: (state) => {
            SecurityUtils.clearToken();
            state.loggedIn = false;
            state.roles = {};
            state.profile = undefined;
        },
        setActiveProfile: (state, action: PayloadAction<User>) => {
            state.profile = action.payload;
        },
        setActiveTaxonomies: (state, action: PayloadAction<Taxonomy[] | undefined>) => {
            state.activeTaxonomies = action.payload;
            if (action.payload) {
                SecurityUtils.setStoredTaxonomies(action.payload);

                return;
            }
            SecurityUtils.clearStoredTaxonomies();
        },
    },
});

// actions
export const { setActiveUser, logoutUser, setActiveProfile, setActiveTaxonomies } = authSlice.actions;

// async actions
export const handleLogin = (username: string, password: string): AppThunk<Observable<AuthContext>> => {
    return (dispatch) =>
        authenticationService.login(username, password).pipe(
            tap((val) => {
                dispatch(setActiveUser(val));
                dispatch(loadProfile());
            }),
        );
};

export const loadProfile = (): AppThunk => (dispatch, getState) => {
    const state = getState();

    if (!state.auth.loggedIn) {
        // only load if we are already logged in
        return;
    }

    userService.loadProfile().subscribe((user) => {
        dispatch(setActiveProfile(user));

        if (
            !state.auth.activeTaxonomies?.every(({ id: activeTaxonomyId }) => {
                return user.taxonomies.some(({ id: userTaxonomyId }) => {
                    return activeTaxonomyId === userTaxonomyId;
                });
            })
        ) {
            // not all currently selected taxonomies are accessible for this user.
            dispatch(setActiveTaxonomies([user.taxonomies[0]]));
        }
    });
};

export const refreshTokens = (): AppThunk<Observable<AuthContext>> => () => {
    const refreshToken = SecurityUtils.getRefreshToken();
    if (!refreshToken) {
        // no refresh token, can't do anything
        const ctxt: AuthContext = { valid: false, roles: {}, taxonomies: [] };

        return of(ctxt);
    }

    return authenticationService.refresh(refreshToken);
};

// selectors
export const selectLoggedIn = (state: RootState): boolean => state.auth.loggedIn;
export const selectRoles = (state: RootState): Record<string, boolean> => state.auth.roles;
export const selectProfile = (state: RootState): User | undefined => state.auth.profile;
export const selectActiveTaxonomies = (state: RootState): Taxonomy[] | undefined => state.auth.activeTaxonomies;
export const selectAvailableTaxonomies = (state: RootState): Taxonomy[] => state.auth.profile?.taxonomies || [];

export const AuthReducer = authSlice.reducer;
